<template>
  <div id="exchange-storage">
    <v-card>
      <v-card-title class="align-start">
        <span>Хранение журналов</span>
        <v-spacer></v-spacer>
        <v-btn
          outlined
          small
          :loading="loading"
          :disabled="saving || purging"
          @click="fetch()"
        >
          <v-icon
            size="18"
            class="me-2"
          >
            {{ icons.mdiRefresh }}
          </v-icon>
          <span>Обновить</span>
        </v-btn>
      </v-card-title>

      <v-card-subtitle class="pb-0">
        Журналы обмена с внешними системами лежат в базе секциями по месяцам. Срок — сколько
        полных месяцев до текущего хранить: секции старше удаляет ночная задача, а «Очистить
        сейчас» делает то же самое сразу. Записанное до разбиения по месяцам лежит одним
        куском и удаляется целиком, когда и самая свежая его запись старше срока. Пустой
        срок — «бессрочно»: из такого журнала не удаляется ничего. Удаление необратимо.
      </v-card-subtitle>

      <v-card-text class="pt-5">
        <v-progress-linear
          v-if="loading && !loaded"
          indeterminate
        ></v-progress-linear>

        <v-alert
          v-if="error"
          type="error"
          dense
          text
          class="mb-0"
        >
          {{ error }}
        </v-alert>

        <div
          v-if="loaded"
          class="d-flex align-center flex-wrap exchange-storage__actions"
        >
          <v-btn
            color="primary"
            small
            :loading="saving"
            :disabled="!canSave"
            @click="save"
          >
            <span>Сохранить сроки</span>
          </v-btn>

          <v-btn
            color="error"
            small
            :loading="purging"
            :disabled="!canPurge"
            @click="purgePopupShow"
          >
            <span>{{ purgeText }}</span>
          </v-btn>

          <span
            v-if="purgeHint"
            class="text--secondary"
          >{{ purgeHint }}</span>
        </div>

        <v-alert
          v-if="saveError"
          type="error"
          dense
          text
          dismissible
          class="mt-4 mb-0"
          @input="clearSaveError"
        >
          {{ saveError }}
        </v-alert>

        <v-alert
          v-if="purgeResult"
          :type="purgeResult.type"
          dense
          text
          dismissible
          class="mt-4 mb-0"
          @input="clearPurgeResult"
        >
          {{ purgeResult.text }}
          <div
            v-for="line in purgeResult.lines"
            :key="line"
          >
            {{ line }}
          </div>
        </v-alert>
      </v-card-text>
    </v-card>

    <template v-if="loaded">
      <v-card
        v-for="card in cards"
        :key="card.key"
        class="mt-6"
      >
        <v-card-text class="d-flex align-start flex-wrap pb-0">
          <div class="pb-4">
            <div class="text-h6 text--primary">
              {{ card.name }}
            </div>
            <div>{{ size(card.sizeBytes) }} · {{ approxRows(card.rows) }}</div>
          </div>

          <v-spacer></v-spacer>

          <v-text-field
            v-if="canUpdate"
            v-model="terms[card.field]"
            type="number"
            min="1"
            :max="maxTerm"
            dense
            outlined
            clearable
            label="Хранить, месяцев"
            placeholder="бессрочно"
            persistent-placeholder
            persistent-hint
            :hint="card.termHint"
            :error-messages="card.termError"
            :disabled="saving || purging"
            class="exchange-storage__term"
          ></v-text-field>
          <div
            v-else
            class="pb-4"
          >
            Хранить: <span class="font-weight-medium text--primary">{{ termText(card.retentionMonths) }}</span>
          </div>
        </v-card-text>

        <v-card-text
          v-if="card.stray"
          class="pb-0"
        >
          <v-alert
            type="warning"
            dense
            text
          >
            Записи попали мимо месячных секций — сообщите разработчикам:
            {{ rowsCount(card.stray.rows) }}, {{ size(card.stray.sizeBytes) }}.
          </v-alert>
        </v-card-text>

        <v-simple-table
          v-if="card.sections.length"
          dense
          class="exchange-storage__sections"
        >
          <thead>
            <tr>
              <th>Период</th>
              <th class="text-right">
                Записей, примерно
              </th>
              <th class="text-right">
                Размер
              </th>
              <th>Можно удалить с</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="section in card.sections"
              :key="section.name"
            >
              <td class="py-2">
                <span class="me-3">{{ section.period }}</span>
                <v-chip
                  v-if="section.willBeRemoved"
                  small
                  label
                  color="error"
                  class="v-chip-light-bg error--text"
                >
                  будет удалено при следующей очистке
                </v-chip>
                <v-chip
                  v-else-if="section.pending"
                  small
                  label
                  outlined
                  color="warning"
                >
                  после сохранения попадёт под удаление
                </v-chip>
                <div
                  v-if="section.newestAt"
                  class="text-caption text--secondary"
                >
                  последняя запись: {{ toDateTimeString(section.newestAt) }}
                </div>
              </td>
              <td class="text-right text-no-wrap">
                {{ rows(section.rows) }}
              </td>
              <td class="text-right text-no-wrap">
                {{ size(section.sizeBytes) }}
              </td>
              <td class="text-no-wrap">
                {{ day(section.removableFrom) || '—' }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-card-text
          v-else
          class="text--secondary"
        >
          Записей в журнале нет.
        </v-card-text>
      </v-card>

      <v-card class="mt-6">
        <v-card-title class="text-h6">
          Последние очистки
        </v-card-title>

        <v-simple-table
          v-if="lastRuns.length"
          dense
        >
          <thead>
            <tr>
              <th>Когда</th>
              <th>Кто</th>
              <th class="text-right">
                Удалено секций
              </th>
              <th class="text-right">
                Освобождено
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="run in lastRuns"
              :key="`${run.ranAt}|${run.by}`"
            >
              <td class="text-no-wrap">
                {{ toDateTimeString(run.ranAt) }}
              </td>
              <td>{{ run.by }}</td>
              <td class="text-right">
                {{ run.removedSections }}
              </td>
              <td class="text-right text-no-wrap">
                {{ size(run.freedBytes) }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-card-text
          v-else
          class="text--secondary"
        >
          Очисток ещё не было.
        </v-card-text>
      </v-card>
    </template>

    <delete-dialog
      ref="savePopup"
      @delete-confirmed="saveTerms"
    >
      <p>С новыми сроками под удаление попадёт:</p>
      <removal-list :journals="pendingRemoval"></removal-list>
      <p class="mb-0">
        Всего {{ size(pendingSize) }}. Ближайшая очистка — сегодня ночью или по кнопке
        «Очистить сейчас» — удалит эти записи безвозвратно.
      </p>
    </delete-dialog>

    <delete-dialog
      ref="purgePopup"
      @delete-confirmed="confirmPurge"
    >
      <p>Будет удалено:</p>
      <removal-list :journals="removal"></removal-list>
      <p class="mb-0">
        Освободится {{ size(removalSize) }}. Вернуть удалённое будет нельзя.
      </p>
    </delete-dialog>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import { mdiRefresh } from '@mdi/js'
import sumBy from 'lodash/sumBy'
import { mapFormHandlers } from '@/helpers/forms'
import DeleteDialog from '@/components/delete-dialog'
import RemovalList from '@/views/exchange-storage/removal-list.vue'
import { hasPermission, toDateTimeString } from '@/helpers'
import {
  MAX_TERM,
  isRemovable,
  isTermValid,
  listedSections,
  sectionsToRemove,
  sectionPeriod,
  sizeOf,
  straySection,
  termField,
  termText,
  toApproxRowsString,
  toDayString,
  toRowsCountString,
  toRowsString,
  toSizeString,
  toTerm,
} from '@/helpers/exchange-storage'

export default {
  name: 'ExchangeStorage',
  components: { DeleteDialog, RemovalList },
  data: () => ({
    icons: { mdiRefresh },
    toDateTimeString,
    termText,
    maxTerm: MAX_TERM,
  }),
  computed: {
    ...mapFormHandlers('exchangeStorage', [
      'terms.delivery',
      'terms.bakai',
      'terms.integration',
    ]),
    ...mapState('exchangeStorage', [
      'journals',
      'lastRuns',
      'currentMonth',
      'loading',
      'loaded',
      'error',
      'saving',
      'saveError',
      'purging',
      'purgeResult',
    ]),
    canUpdate() {
      return hasPermission('Permissions.ExchangeStorageUpdate')
    },

    cards() {
      return this.journals.map(journal => {
        const field = termField(journal.key)
        const term = toTerm(this.terms[field])
        const valid = isTermValid(term)
        const changed = term !== journal.retentionMonths

        return {
          ...journal,
          field,
          term,
          changed,
          termError: valid ? '' : `От 1 до ${MAX_TERM} месяцев, пусто — бессрочно`,
          termHint: changed && valid ? `Не сохранено. Действует: ${termText(journal.retentionMonths)}` : '',
          stray: straySection(journal.sections),
          sections: listedSections(journal.sections, this.currentMonth).map(section => ({
            ...section,
            period: sectionPeriod(section),
            pending: changed
              && valid
              && !section.willBeRemoved
              && isRemovable(section, term, this.currentMonth),
          })),
        }
      })
    },
    changed() {
      return this.cards.some(card => card.changed)
    },
    busy() {
      return this.loading || this.saving || this.purging
    },
    canSave() {
      return this.canUpdate && this.changed && !this.cards.some(card => card.termError) && !this.busy
    },

    pendingRemoval() {
      return this.cards
        .map(card => ({ card, sections: card.sections.filter(section => section.pending) }))
        .filter(({ sections }) => sections.length)
        .map(({ card, sections }) => ({
          key: card.key,
          title: `${card.name} — хранить ${termText(card.term)}`,
          sizeBytes: sizeOf(sections),
          sections,
        }))
    },
    pendingSize() {
      return sumBy(this.pendingRemoval, 'sizeBytes')
    },

    removal() {
      return this.journals
        .map(journal => ({ journal, sections: sectionsToRemove(journal.sections) }))
        .filter(({ sections }) => sections.length)
        .map(({ journal, sections }) => ({
          key: journal.key,
          title: journal.name,
          sizeBytes: sizeOf(sections),
          sections: sections.map(section => ({ ...section, period: sectionPeriod(section) })),
        }))
    },
    removalSize() {
      return sumBy(this.removal, 'sizeBytes')
    },

    canPurge() {
      return hasPermission('Permissions.ExchangeStoragePurge')
        && this.removal.length > 0
        && !this.changed
        && !this.busy
    },
    purgeText() {
      return this.removal.length ? `Очистить сейчас — ${toSizeString(this.removalSize)}` : 'Очистить сейчас'
    },
    purgeHint() {
      if (this.changed) return 'Сначала сохраните сроки: очистка идёт по сохранённым.'

      return this.removal.length ? '' : 'Под удаление сейчас ничего не попадает.'
    },
  },
  async created() {
    document.title = 'Хранение журналов'
    await this.fetch()
  },
  methods: {
    ...mapActions('exchangeStorage', { fetch: 'fetch', saveTerms: 'save', purge: 'purge' }),
    ...mapMutations('exchangeStorage', {
      setSaveError: 'SET_SAVE_ERROR',
      setPurgeResult: 'SET_PURGE_RESULT',
    }),
    size(bytes) {
      return toSizeString(bytes)
    },
    rows(count) {
      return toRowsString(count)
    },
    rowsCount(count) {
      return toRowsCountString(count)
    },
    approxRows(count) {
      return toApproxRowsString(count)
    },
    day(value) {
      return toDayString(value)
    },
    clearSaveError() {
      this.setSaveError(null)
    },
    clearPurgeResult() {
      this.setPurgeResult(null)
    },

    async save() {
      if (this.pendingRemoval.length) {
        this.$refs.savePopup.open()

        return
      }

      await this.saveTerms()
    },

    purgePopupShow() {
      this.$refs.purgePopup.open()
    },

    async confirmPurge() {
      await this.purge(this.removal.flatMap(journal => journal.sections.map(section => section.name)))
    },
  },
}
</script>

<style lang="scss" scoped>
#exchange-storage {
  .exchange-storage__actions {
    gap: 12px;
  }

  .exchange-storage__term {
    flex: none;
    width: 280px;
  }

      .exchange-storage__sections ::v-deep th {
    &:nth-child(2),
    &:nth-child(4) {
      width: 170px;
    }

    &:nth-child(3) {
      width: 110px;
    }
  }
}
</style>
